import type { IssueCode } from '../core/model/issue-codes.ts';

/**
 * The application's own vocabulary.
 *
 * English is the source of truth: its keys define the type every other locale must satisfy, so a
 * missing translation is a build error rather than a blank label discovered by a user.
 *
 * Placeholders are `{name}` and are substituted at render time. Diagnostics are keyed separately,
 * by issue code, because the core produces codes and never sentences — that is what lets the same
 * validation run in three languages without three copies of the rules.
 */

export const en = {
  'app.name': 'FreExam Player',
  'app.tagline': 'Run JSON-based practice exams entirely in your browser.',
  'app.privacy':
    'Your exam files and answers are processed locally in your browser and are not uploaded to any server.',
  'app.skipToContent': 'Skip to main content',
  'app.textSizeValue': '{percent}%',

  'import.title': 'Open an exam',
  'import.instructions': 'Choose a .json exam file from your device, or drop one here.',
  'import.choose': 'Choose a file',
  'import.dropHere': 'Drop the file here',
  'import.reading': 'Reading the file…',
  'import.validating': 'Checking the file…',
  'import.example': 'Try an example exam',
  'import.formatHelp': 'What is an exam file?',
  'import.downloadTemplate': 'Download the schema template',
  'import.downloadTemplateHint': 'Give it to any AI along with your material to build an exam file.',
  'import.again': 'Open a different file',

  'exam.loaded': 'Loaded {title}',
  'exam.questionCount': '{count} questions',
  'exam.license': 'Licence: {license}',
  'exam.licenseUnknown': 'Licence not declared',
  'exam.source': 'Source: {source}',
  'exam.warnings': 'Loaded with {count} warnings',
  'exam.viewReport': 'View report',

  'report.title': 'This file cannot be used',
  'report.titleWarnings': 'The file loaded, with things worth checking',
  'report.summary': '{errors} errors, {warnings} warnings',
  'report.download': 'Download validation-report.json',
  'report.copy': 'Copy report',
  'report.copied': 'Copied',
  'report.tryAnother': 'Try another file',
  'report.suppressed': 'and {count} more of the same kind',
  'report.question': 'Question {id}',
  'report.expected': 'Expected',
  'report.received': 'Found',
  'report.location': 'Location',
  'report.noIssues': 'No problems found.',
  'report.severity.error': 'Error',
  'report.severity.warning': 'Warning',
  'report.severity.info': 'Note',

  'config.title': 'Set up your session',
  'config.mode': 'Mode',
  'config.mode.exam': 'Exam',
  'config.mode.exam.description': 'Timed, nothing revealed until you submit.',
  'config.mode.practice': 'Practice',
  'config.mode.practice.description': 'No clock, answers and explanations as you go.',
  'config.mode.custom': 'Custom',
  'config.mode.custom.description': 'Choose each setting yourself.',
  'config.questionCount': 'Number of questions',
  'config.all': 'All ({count})',
  'config.matching': '{count} questions match your filters',
  'config.matchingOne': '1 question matches your filters',
  'config.noMatches': 'No questions match these filters.',
  'config.filters': 'Filters',
  'config.category': 'Category',
  'config.topic': 'Topic',
  'config.difficulty': 'Difficulty',
  'config.type': 'Question type',
  'config.tag': 'Tag',
  'config.seed': 'Seed',
  'config.seedHelp': 'The same seed draws the same questions in the same order.',
  'config.newSeed': 'New seed',
  'config.timeLimit': 'Time limit (minutes)',
  'config.noTimeLimit': 'No time limit',
  'config.shuffleQuestions': 'Shuffle questions',
  'config.shuffleOptions': 'Shuffle options',
  'config.immediateFeedback': 'Show the answer after each question',
  'config.allowBack': 'Allow going back',
  'config.allowChange': 'Allow changing answers',
  'config.start': 'Start',
  'config.back': 'Back',
  'config.candidateName': 'Your name',
  'config.candidateNamePlaceholder': 'Optional',
  'config.candidateNameHelp':
    'Shown while you take the exam and written into the results you export. It stays on your device.',
  'config.timer': 'Timer',
  'config.timerOn': 'Timer on',
  'config.useRange': 'Only a range of questions',
  'config.rangeFrom': 'From',
  'config.rangeTo': 'To',
  'config.rangeHelp': 'Numbered as they appear in the file, 1 to {total}.',
  'config.weakest': 'What you got wrong',
  'config.onlyIncorrect': 'Only questions I have answered incorrectly ({count} available)',
  'config.incorrectAtLeast': 'At least this many times',
  'config.device': 'This device',
  'config.remember': 'Remember my progress on this device',
  'config.rememberHelp':
    'Keeps track of which questions you get wrong, and lets you save a session and come back to it. Stored in this browser, never uploaded. Off unless you turn it on.',
  'config.remembered': '{count} questions are remembered as answered incorrectly.',
  'config.forget': 'Forget everything on this device',

  'difficulty.easy': 'Easy',
  'difficulty.medium': 'Medium',
  'difficulty.hard': 'Hard',

  'type.single-choice': 'Single choice',
  'type.multiple-choice': 'Multiple choice',
  'type.matching': 'Matching',
  'type.ordering': 'Ordering',
  'type.text': 'Written answer',

  'session.question': 'Question {position} of {total}',
  'session.previous': 'Previous',
  'session.next': 'Next',
  'session.mark': 'Mark for review',
  'session.marked': 'Marked for review',
  'session.report': 'Report a problem',
  'session.submit': 'Submit',
  'session.navigator': 'Questions',
  'session.timeRemaining': 'Time remaining',
  'session.timeUp': 'Time is up',
  'session.paused': 'Paused',
  'session.selectCount': 'Choose {count}',
  'session.selectRange': 'Choose between {min} and {max}',
  'session.answerHere': 'Your answer',
  'session.checkAnswer': 'Check answer',
  'session.correct': 'Correct',
  'session.incorrect': 'Not correct',
  'session.explanation': 'Explanation',
  'session.references': 'References',
  'session.status.answered': 'answered',
  'session.status.unanswered': 'not answered',
  'session.status.marked': 'marked for review',
  'session.status.current': 'current question',
  'session.moveUp': 'Move up',
  'session.moveDown': 'Move down',
  'session.position': 'Position {position}',
  'session.matchWith': 'Match with',
  'session.chooseTarget': 'Choose…',
  'session.sourceNumber': 'File Q{number}',
  'session.pause': 'Pause',
  'session.resume': 'Resume',
  'session.pausedHelp': 'The clock is stopped and the questions are hidden.',
  'session.save': 'Save session',
  'session.saved': 'Session saved on this device.',
  'session.saveFailed': 'This session could not be saved on this device.',
  'session.review': 'Review',
  'session.reviewAll': 'All ({count})',
  'session.reviewMarked': 'Marked ({count})',
  'session.reviewUnanswered': 'Unanswered ({count})',
  'session.goToNext': 'Go to next',
  'session.reviewEmpty': 'Nothing here.',

  'note.add': 'Add a note',
  'note.label': 'Your note about this question',
  'note.placeholder': 'What confused you, what to look up, what you want to remember.',
  'note.help': 'Notes stay on your device. You can read them all together when you finish.',
  'note.remaining': '{count} characters left.',
  'note.summaryTitle': 'Your notes ({count})',
  'note.export': 'Export notes',
  'note.includeQuestions': 'Include the questions, so I can study from this file',
  'note.exportHelp':
    'A readable Markdown file with your notes and the question numbers they belong to. It does not contain the questions themselves.',
  'note.exportHelpWithQuestions':
    'A readable Markdown file with each noted question, the options as you saw them, your answer, the right answer and the explanation. It contains the exam content, so treat it the way the exam licence asks you to.',

  'controls.textSize': 'Text size',
  'controls.textSize.description': 'Make the questions larger or smaller.',
  'controls.language': 'Language',
  'controls.language.description': 'The language of this interface, not of the exam.',
  'controls.focus': 'Focus mode',
  'controls.focus.description': 'Show only the current question.',
  'controls.theme': 'Theme',
  'controls.theme.description': 'Light, dark, or whatever your device uses.',
  'controls.theme.system': 'Match my device',
  'controls.theme.light': 'Light',
  'controls.theme.dark': 'Dark',

  'correct.open': 'Correct this question',
  'correct.title': 'What it should say',
  'correct.help':
    'Only you can see this. When you are done, the results screen offers a corrected copy of the exam to send to whoever wrote it.',
  'correct.reason': 'What is wrong',
  'correct.reason.incorrect-answer': 'The marked answer is wrong',
  'correct.reason.incorrect-explanation': 'The explanation is wrong',
  'correct.reason.typo': 'A typo',
  'correct.reason.media-description': 'The image needs describing',
  'correct.reason.ambiguous-question': 'The question is ambiguous',
  'correct.reason.outdated-content': 'The content is out of date',
  'correct.reason.conversion-error': 'Something was lost in conversion',
  'correct.reason.translation-error': 'The translation is wrong',
  'correct.reason.other': 'Something else',
  'correct.answer': 'The right answer',
  'correct.optionText': 'The wording of each option',
  'correct.explanation': 'The explanation',
  'correct.mediaDescription': 'What the image showed: {alt}',
  'correct.note': 'Anything else the author should know',
  'correct.discard': 'Discard this correction',
  'correct.export': 'Export corrected exam',
  'correct.exportHelp':
    'A complete exam file with your corrections, recording which exam it came from and what you changed. It contains the exam content, so the exam licence governs what you may do with it.',

  'update.available': 'A new version of the player is ready.',
  'update.apply': 'Reload now',
  'update.later': 'Not now',

  'focus.open': 'Focus',
  'focus.title': 'Focus mode',
  'focus.exit': 'Close',

  'calculator.title': 'Calculator',
  'calculator.close': 'Close',
  'calculator.error': 'Cannot divide by zero',

  'resume.title': 'You have a saved session for this exam',
  'resume.draftTitle': 'You were in the middle of this exam',
  'resume.detail': '{answered} of {total} questions answered.',
  'resume.continue': 'Continue it',
  'resume.discard': 'Discard it',

  'media.wasImage': 'The original question contained an image.',
  'media.unavailable': 'The image is not included in this file.',
  'media.remote': 'The image is hosted elsewhere and is not loaded automatically.',
  'media.description': 'Description',

  'submit.title': 'Submit your answers?',
  'submit.complete': 'You have answered every question.',
  'submit.incomplete': '{count} questions are not answered.',
  'submit.marked': '{count} questions are marked for review.',
  'submit.confirm': 'Submit',
  'submit.cancel': 'Keep going',

  'results.title': 'Results',
  'results.score': '{percent}%',
  'results.scoreOf': '{raw} of {max} points',
  'results.passed': 'Passed',
  'results.failed': 'Not passed',
  'results.correct': 'Correct',
  'results.incorrect': 'Incorrect',
  'results.unanswered': 'Unanswered',
  'results.partial': 'Partial',
  'results.ungraded': 'Not graded automatically',
  'results.ungradedHelp':
    'These are written answers the app cannot mark. Review them and decide for yourself.',
  'results.duration': 'Time taken',
  'results.averagePerQuestion': 'Average per question',
  'results.byCategory': 'By category',
  'results.byDifficulty': 'By difficulty',
  'results.byType': 'By question type',
  'results.byTag': 'By tag',
  'results.review': 'Review answers',
  'results.export': 'Export results',
  'results.newSession': 'New session',
  'results.markedCount': 'Marked for review',
  'results.yourAnswer': 'Your answer',
  'results.correctAnswer': 'Correct answer',
  'results.markCorrect': 'I got this right',
  'results.markIncorrect': 'I got this wrong',
  'results.noAnswer': 'No answer given',

  'reportQuestion.title': 'Report a problem with this question',
  'reportQuestion.help':
    'This stays on your device. It is saved with your exported results, and can be used to build a corrected copy of the exam.',
  'reportQuestion.reason.incorrect-answer': 'The marked answer is wrong',
  'reportQuestion.reason.ambiguous-question': 'The question is ambiguous',
  'reportQuestion.reason.incorrect-explanation': 'The explanation is wrong',
  'reportQuestion.reason.outdated-content': 'The content is out of date',
  'reportQuestion.reason.conversion-error': 'Something was lost in conversion',
  'reportQuestion.reason.translation-error': 'The translation is wrong',
  'reportQuestion.reason.other': 'Something else',
  'reportQuestion.comment': 'Anything else? (optional)',
  'reportQuestion.save': 'Save report',
  'reportQuestion.cancel': 'Cancel',
  'reportQuestion.saved': 'Report saved',

  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.of': 'of',
  'common.minutes': '{count} min',
  'common.seconds': '{count} s',
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;

/**
 * One sentence per diagnostic code.
 *
 * `Record<IssueCode, string>` is the point: adding a code to the core does not compile until every
 * locale can say it. A validator that produces a code nobody can read is a validator that says
 * nothing.
 */
export type IssueMessages = Record<IssueCode, string>;

export const enIssues: IssueMessages = {
  FILE_EMPTY: 'The file is empty.',
  FILE_TOO_LARGE: 'The file is larger than this app will open ({expected}).',
  FILE_UNSUPPORTED_EXTENSION: 'Only {expected} files can be opened.',
  FILE_READ_FAILED: 'The file could not be read. It may have been moved or renamed.',
  FILE_NOT_UTF8: 'The file is not valid UTF-8 text. Try saving it again as UTF-8.',

  SEC_DEPTH_EXCEEDED: 'The file nests too deeply to be a reasonable exam.',
  SEC_NODES_EXCEEDED: 'The file contains far more data than an exam should.',
  SEC_PROTO_POLLUTION_KEY: 'Unsafe keys ({received}) were removed while reading the file.',
  SEC_UNSAFE_URL: 'A URL in the file uses a scheme that is not allowed.',
  SEC_MEDIA_NOT_ALLOWED_TYPE: 'An embedded image uses a format that is not allowed.',
  SEC_MEDIA_TYPE_MISMATCH: 'An embedded image does not match the type it declares ({received}).',
  SEC_MEDIA_INVALID_BASE64: 'An embedded image is not valid base64.',
  SEC_MEDIA_TOO_LARGE: 'An embedded image is larger than allowed.',
  SEC_MEDIA_DIMENSIONS_TOO_LARGE:
    'An embedded image declares more pixels than can safely be drawn. The file is small, but decoding it would not be.',
  SEC_MEDIA_HEADER_UNREADABLE:
    'An embedded image has a header this player cannot read, so its size cannot be checked before decoding it.',

  JSON_SYNTAX_ERROR: 'The file is not valid JSON (line {line}, column {column}).',
  JSON_ROOT_NOT_OBJECT: 'An exam file must be a JSON object, but this is {received}.',

  DETECT_MISSING_KIND: 'The file does not say what it is. Add "kind": "exam" at the top.',
  DETECT_UNKNOWN_KIND: 'This is not a kind of file this app knows ({received}).',
  DETECT_WRONG_KIND: 'This is a session results file, not an exam.',
  DETECT_INVALID_VERSION: 'The schemaVersion is not a version number ({received}).',
  DETECT_UNSUPPORTED_MAJOR: 'This file uses format {received}; this app understands {expected}.',
  DETECT_NEWER_MINOR:
    'This file was written for a newer version of the format ({received}). Anything unfamiliar will be ignored.',

  SCHEMA_REQUIRED_PROPERTY_MISSING: 'Missing {expected}.',
  SCHEMA_UNKNOWN_PROPERTY: 'Unexpected property {received}.',
  SCHEMA_WRONG_TYPE: 'Expected {expected}, found {received}.',
  SCHEMA_VALUE_NOT_ALLOWED: 'Expected {expected}, found {received}.',
  SCHEMA_PATTERN_MISMATCH: 'Expected {expected}, found {received}.',
  SCHEMA_TEXT_LENGTH_OUT_OF_RANGE: 'Expected {expected}, found {received}.',
  SCHEMA_ARRAY_LENGTH_OUT_OF_RANGE: 'Expected {expected}, found {received}.',
  SCHEMA_DUPLICATE_ITEMS: 'Expected {expected}: {received}.',
  SCHEMA_NUMBER_OUT_OF_RANGE: 'Expected a number {expected}, found {received}.',
  SCHEMA_FORBIDDEN_COMBINATION: 'These fields cannot be combined this way.',
  SCHEMA_CONSTRAINT_VIOLATED: 'This value does not satisfy the schema.',
  SCHEMA_CONTENT_HASH_NOT_AUTHORABLE:
    'contentHash cannot be written into the file: it is computed from the file itself. Remove it.',

  MIGRATION_NO_PATH: 'This file cannot be converted from {expected}.',
  MIGRATION_FIELD_DROPPED: 'A field was dropped while converting the file.',
  MIGRATION_PRODUCED_INVALID_DOCUMENT:
    'Converting this file produced something invalid. This is a bug in the app, not in your file.',

  SEM_DUPLICATE_QUESTION_ID: 'Two questions share the same id: {received}.',
  SEM_DUPLICATE_OPTION_ID: 'Two options in this question share the same id: {received}.',
  SEM_UNKNOWN_CORRECT_OPTION: 'The correct answer {received} is not one of the options.',
  SEM_UNKNOWN_TAXONOMY_REF: 'The category {received} is not declared in the taxonomy.',
  SEM_TAXONOMY_NOT_DECLARED:
    'Questions are classified but the exam declares no taxonomy, so categories cannot be checked.',
  SEM_TAXONOMY_UNUSED_NODE: 'A declared category or topic is used by no question.',

  SEM_MC_SELECTION_MISMATCH: 'The number of answers to choose does not match the answer key.',
  SEM_MC_ALL_OPTIONS_CORRECT: 'Every option is marked correct.',
  SEM_MC_SINGLE_CORRECT_ANSWER:
    'Only one answer is correct; this could be a single-choice question.',
  SEM_MATCHING_ITEM_WITHOUT_PAIR: 'An item has no answer: {received}.',
  SEM_MATCHING_DUPLICATE_ITEM_PAIR: 'An item is matched more than once: {received}.',
  SEM_MATCHING_TARGET_REUSED: 'A target is used more than once: {received}.',
  SEM_MATCHING_UNKNOWN_REF: 'The answer key refers to something that does not exist: {received}.',
  SEM_MATCHING_NO_DISTRACTOR: 'There are as many targets as items, so the last pair is free.',
  SEM_ORDERING_NOT_PERMUTATION: 'The correct order does not list every item exactly once.',
  SEM_ORDERING_IDENTITY: 'The items are already in the correct order as written.',
  SEM_TEXT_AUTO_WITHOUT_ACCEPTED: 'This question is marked as auto-scored but has no answers.',
  SEM_TEXT_ACCEPTED_COLLIDES: 'Two accepted answers are the same once normalised.',
  SEM_TEXT_SELF_WITHOUT_SAMPLE: 'A self-marked question with nothing to compare against.',

  SEM_MEDIA_DUPLICATE_ID: 'Two images in this question share the same id.',
  SEM_MEDIA_MISSING_ALT: 'An image has no alternative text.',
  SEM_MEDIA_ESSENTIAL_WITHOUT_DESCRIPTION:
    'The question depends on an image that has no description, so it cannot be answered without it.',
  SEM_MEDIA_UNRESOLVABLE: 'An image will not be shown ({received}); its description will be used.',
  SEM_MEDIA_TOTAL_SIZE_EXCEEDED: 'The embedded images add up to more than allowed ({received}).',

  SEM_UNSAFE_URL: 'A link is not a plain https URL ({received}).',
  SEM_EXPLANATION_MISSING: 'This question has no explanation.',
  SEM_OPTION_TEXT_DUPLICATE: 'Two options read the same.',
  SEM_OPTION_LOOKS_LIKE_LETTER:
    'An option id looks like a display letter ({received}); use a stable id instead.',
  SEM_PROMPT_CONTAINS_MARKUP: 'This text contains markup, which will be shown literally.',
  SEM_PROMPT_REFERENCES_MISSING_IMAGE: 'The question mentions an image but declares none.',
  SEM_LANGUAGE_MISMATCH: 'This question is in a different language from the exam.',
  SEM_LICENSE_NOT_SPDX: 'The licence {received} is not a recognised SPDX identifier.',
  SEM_LICENSE_NOT_DECLARED: 'The exam does not declare a licence.',
  SEM_INVALID_DATE: 'A date does not exist: {received}.',
};

/** Actionable suggestions, shown under the message they belong to. */
export const enHints: Record<string, string> = {
  FILE_READ_FAILED_HINT_RESELECT: 'Choose the file again.',
  FILE_NOT_UTF8_HINT_RESAVE: 'Open it in a text editor and save it as UTF-8.',
  JSON_SYNTAX_HINT_TRUNCATED: 'The file looks cut short — check that it downloaded completely.',
  DETECT_MISSING_KIND_HINT_ADD: 'Add "kind": "exam" as the first property.',
  DETECT_WRONG_KIND_HINT_SESSION_FILE: 'Open it from the results screen instead.',
  DETECT_UNSUPPORTED_MAJOR_HINT_UPDATE_APP: 'Update the app to open this file.',
  DETECT_UNSUPPORTED_MAJOR_HINT_TOO_OLD: 'This format version is no longer supported.',
  SCHEMA_CONTENT_HASH_HINT_REMOVE: 'Delete the contentHash property.',
  SCHEMA_UNKNOWN_PROPERTY_HINT_TYPO: 'Check the spelling against the schema.',
  SEM_UNKNOWN_CORRECT_OPTION_HINT: 'Fix the answer key, or add the missing option.',
  SEM_UNKNOWN_TAXONOMY_REF_HINT_DID_YOU_MEAN: 'Did you mean {suggestion}?',
  SEM_TAXONOMY_NOT_DECLARED_HINT: 'Declare exam.taxonomy so categories can be checked.',
  SEM_MC_ALL_OPTIONS_CORRECT_HINT: 'Check the answer key — this is usually a conversion mistake.',
  SEM_ORDERING_IDENTITY_HINT: 'List the items in a different order from the answer.',
  SEM_TEXT_SELF_WITHOUT_SAMPLE_HINT: 'Add a sampleAnswer for the candidate to compare against.',
  SEM_MEDIA_ESSENTIAL_WITHOUT_DESCRIPTION_HINT:
    'Describe what the image showed, in enough detail to answer from.',
  SEM_MEDIA_UNRESOLVABLE_HINT_REMOTE: 'Embed the image, or rely on its description.',
  SEM_MEDIA_UNRESOLVABLE_HINT_PACKAGE: 'Exam packages are not supported yet.',
  SEM_OPTION_LOOKS_LIKE_LETTER_HINT: 'Use ids like "opt-1"; letters are assigned by the player.',
  SEM_PROMPT_CONTAINS_MARKUP_HINT: 'Schema 1.0 has no Markdown or HTML — write plain text.',
  SEM_PROMPT_REFERENCES_MISSING_IMAGE_HINT: 'Add a media entry describing the image.',
  SEM_LICENSE_NOT_DECLARED_HINT: 'Add exam.license, for example "CC-BY-4.0".',
};
